import styled from '@emotion/styled';
import { useRef, useEffect } from 'react';

import { ToastManager } from '../../../ToastManager';
import { IconButton } from '../../buttons/IconButton';
import refresh from '../images/button/refresh.svg';
import default_camera_image from '../images/camera/default_camera_image.png';

import { useEventCallback } from '../../../hooks/useEventCallback';
import { useImage } from '../../../store/images/useImages';

import { su } from '../../../theme/functions/su.ts';
import { theme } from '../../../theme/theme';

interface ImageData {
  url: string;
  lastUpdated?: string;
}

export interface SnapshotViewProps {
  source?: {
    imageId?: string | null;
  };
  baseUrl?: string;
}

export function SnapshotView(props: SnapshotViewProps) {
  const imageRef = useRef<HTMLImageElement>(null);

  const { image } = useImage({ imageId: props.source?.imageId ?? undefined });

  const fetchImage = useEventCallback(() => {
    if (image == null) {
      return;
    }

    const imageUrl = `${props.baseUrl}${image.url}?lastUpdated=${getLastUpdated(image)}`;

    fetch(imageUrl)
      .then(async (res) => {
        if (imageRef.current == null) {
          return;
        }

        if (res.ok) {
          imageRef.current.src = imageUrl;
          return;
        }

        await res.json().then((errorData) => {
          if (imageRef.current != null) {
            imageRef.current.src = default_camera_image;
          }
          ToastManager.handleError(errorData);
        });
      })
      .catch((err) => {
        if (imageRef.current != null) {
          imageRef.current.src = default_camera_image;
        }
        console.error('SnapshotView: Failed to load the image', err);
        ToastManager.handleError({ message: 'An error occurred while loading the image.' });
      });
  });

  useEffect(() => {
    fetchImage();
  }, [image, fetchImage]);

  return (
    <S.Container>
      <S.Image
        ref={imageRef}
        src={default_camera_image}
        onError={() => {
          if (imageRef.current != null) {
            imageRef.current.src = default_camera_image;
          }
          ToastManager.handleError({ message: 'An error occurred while loading the image.' });
        }}
      />

      <S.Controls>
        <IconButton url={refresh} color={theme.color.white} onPress={fetchImage} />
      </S.Controls>
    </S.Container>
  );
}

function getLastUpdated(image: ImageData): number {
  if (image.lastUpdated) {
    return new Date(image.lastUpdated).getTime();
  }
  return new Date().getTime();
}

function S() {}
SnapshotView.S = S;

S.Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

S.Image = styled.img`
  width: 320px;
  aspect-ratio: 16 / 9;
  border-radius: ${theme.borderRadius.default};
  margin-bottom: ${su(2)};
  object-fit: contain;
  background-color: ${theme.color.black};
`;

S.Controls = styled.div`
  margin-top: 28px;
  margin-bottom: ${su(2)};
`;
